import apiService from '../../../services/api-client';
import { DeleteCompanyResponse } from '../interfaces/delete-company';
import { DeleteUserResponse } from '../interfaces/delete-user';
import { GetCompanyResponse } from '../interfaces/get-company';
import { GetUsersResponse } from '../interfaces/get-users';
import {
  GetCompaniesParams,
} from '../interfaces/get-companies';
import {
  PostCompanyPayloadRequest,
  PostCompanyResponse,
} from '../interfaces/post-company';
import {
  PostUserPayloadRequest,
  PostUserResponse,
} from '../interfaces/post-user';
import {
  PutCompanyPayloadRequest,
  PutCompanyResponse,
} from '../interfaces/put-company';
import { PostVerifyDbPayloadRequest, PostVerifyDbResponse } from '../interfaces/post-verify-db';
import { GetImportationsResponse } from '../interfaces/get-importations';
import { GetStoresResponse } from '../interfaces/get-stores';
import { PostStorePayloadRequest, PostStoreResponse } from '../interfaces/post-store';
import { DeleteStoreResponse } from '../interfaces/delete-store';
import { PutStorePayloadRequest, PutStoreResponse } from '../interfaces/put-store';
import { PostUserStoreResponse } from '../interfaces/post-user-store';
import { DeleteUserStoreResponse } from '../interfaces/delete-user-store';
import { PostVerifyS3PayloadRequest, PostVerifyS3Response } from '../interfaces/post-verify-s3';
import { Company } from '../../../interfaces/company.interface';
import { Store } from '../../../interfaces/store.interface';
import { Integration } from '../../../interfaces/integration.interface';
import { CompanyIntegrationUpdatePayload } from '../interfaces/put-integrations';
import { daDK } from '@mui/x-date-pickers/locales';

export const companiesApiService = {
  async getMeCompanies(params: GetCompaniesParams) {
    const { data } = await apiService().get<Company[]>(
      `companies/me/companies`,
      {
        params: { ...params },
      }
    );
    return data;
  },
  async getCompanies(params: GetCompaniesParams) {
    const { data } = await apiService().get<Company[]>(`companies`, {
      params: { ...params },
    });
    return data;
  },
  async getCompany(id: string) {
    const { data } = await apiService().get<GetCompanyResponse>(
      `companies/${id}`
    );
    return data;
  },
  async getMeCompany(id: string) {
    const { data } = await apiService().get<GetCompanyResponse>(
      `companies/${id}`
    );
    return data;
  },
  async postCompany(payload: PostCompanyPayloadRequest): Promise<PostCompanyResponse> {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('rut', payload.rut);
    if (payload.file) {
      formData.append('file', payload.file);
    }
    const { data } = await apiService().post<PostCompanyResponse>(
      '/create_company', 
      formData
    );
    return data;
  },
  async putCompany(id: string, body: PutCompanyPayloadRequest) {
    const formData = new FormData();
    if (body.name) {
      formData.append('name', body.name);
    }
    if (body.rut) {
      formData.append('rut', body.rut);
    }
    if (body.selectedAvatar) {
      formData.append('selectedAvatar', body.selectedAvatar);
    }
    const { data } = await apiService().put<PutCompanyResponse>(`companies/${id}`, formData);

    return data;
  },
  async deleteCompany(id: string) {
    const { data } = await apiService().delete<DeleteCompanyResponse>(
      `companies/${id}`
    );
    return data;
  },
  async getUsers(id: string) {
    const { data } = await apiService().get<GetUsersResponse>(
      `companies/${id}/users`,
    );
    return data;
  },
  async postUser(id: string, body: PostUserPayloadRequest) {
    const { data } = await apiService().post<PostUserResponse>(
      `companies/${id}/user`,
      body
    );
    return data;
  },
  async deleteUser(id: string, uid: string) {
    const { data } = await apiService().delete<DeleteUserResponse>(
      `companies/${id}/user/${uid}`
    );
    return data;
  },
  async getStores(id: string) {
    const { data } = await apiService().get<Store[]>(
      `/warehouses?company_id=${id}`,
    );
    return data;
  },
  async postStore(companyId: string, body: PostStorePayloadRequest) {
    const { data } = await apiService().post<PostStoreResponse>(
        `/warehouses?company_id=${companyId}`, body
    );
    return data;
  },
  async putStore(companyId: string, storeId: string, body: PutStorePayloadRequest) {
    const { data } = await apiService().put<PutStoreResponse>(
        `/warehouses/${storeId}?company_id=${companyId}`, body
    );
    return data;
  },
  async deleteStore(companyId: string, storeId: string) {
    const { data } = await apiService().delete<DeleteStoreResponse>(
        `/warehouses/${storeId}?company_id=${companyId}`
    );
    return data;
  },
  async postUserStore(id: string, uid: string, sid: string) {
    const { data } = await apiService().post<PostUserStoreResponse>(
      `companies/${id}/stores/${sid}/user/${uid}`);
    return data;
  },
  async deleteUserStore(id: string, uid: string, sid: string) {
    const { data } = await apiService().delete<DeleteUserStoreResponse>(
      `companies/${id}/stores/${sid}/user/${uid}`);
    return data;
  },
  async getImportations(id: string) {
    const { data } = await apiService().get<GetImportationsResponse>(
      `companies/${id}/importations`,
    );
    return data;
  },
  
  async getIntegrations(): Promise<Integration[]> {
    const { data } = await apiService().get<Integration[]>('/integrations');
    return data;
  },

  async updateCompanyIntegration(companyId: string, payload: CompanyIntegrationUpdatePayload): Promise<Company> {
    const { data } = await apiService().put<Company>(`/companies/${companyId}/integration`, {
      integration_id:payload.integrationId,
      api_key:payload.apiKey
    });
    return data;
  },

  async postVerifyDb(body: PostVerifyDbPayloadRequest) {
    const { data } = await apiService().post<PostVerifyDbResponse>(
      `companies/verify-db`,
      body
    );
    return data;
  },
  async postVerifyS3(body: PostVerifyS3PayloadRequest) {
    const { data } = await apiService().post<PostVerifyS3Response>(
      `companies/verify-s3`,
      body
    );
    return data;
  },
};
